import React, { useEffect, useState } from "react";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { useFirebase } from "containers/FirebaseContext";
import { InfoCircleOutlined } from "@ant-design/icons";
import { Popover, Typography } from "antd";
import useGetStudentFromClass from "components/hooks/useGetStudentFromClass/useGetStudentFromClass";
import { useCurrentClass } from "store/classes/selectors";

const JoinedStudents = ({ debateResponse }) => {
  const { firestore } = useFirebase();
  const { classUsers } = useGetStudentFromClass();
  const [activeStudents, setActiveStudents] = useState([]);
  const currentClass = useCurrentClass();

  useEffect(() => {
    if (!debateResponse?.id || !firestore) {
      setActiveStudents([]);
      return;
    }

    const presenceQuery = query(
      collection(firestore, "debate_presence"),
      where("debate_id", "==", debateResponse?.id),
      where("status", "==", "joined"),
      where("class_id", "==", currentClass?.id)
    );

    const unsubscribe = onSnapshot(
      presenceQuery,
      (presenceSnap) => {
        const students =
          presenceSnap?.docs?.map((docSnap) => ({
            id: docSnap.id,
            ...docSnap.data(),
          })) ?? [];
        setActiveStudents(students);
      },
      (error) => {
        console.log("Error getting active student count:", error);
        setActiveStudents([]);
      }
    );

    return () => {
      if (unsubscribe && typeof unsubscribe === "function") {
        unsubscribe();
      }
    };
  }, [firestore, debateResponse?.id]);

  const activeByUserId = new Map(
    (activeStudents || [])
      .filter((s) => s?.user_id)
      .map((s) => [s.user_id, s])
  );

  const activeUserIds = new Set(activeByUserId.keys());

  const studentUser = classUsers?.length > 0 ? classUsers?.filter((user) => user.role === 'student' && user.active) : [];

  const classStudents = debateResponse?.class_id
    ? studentUser || []
    : [];

  const formatStudentName = (student) => {
    const userName = student?.user_name || student?.userName || student?.name;
    if (userName) return userName;

    const first = student?.firstName || "";
    const last = student?.lastName || "";
    const combined = `${first} ${last}`.trim();
    if (combined) return combined;

    return student?.id ? String(student.id) : "Unknown student";
  };

  const sortedClassStudents = [...classStudents].sort((a, b) => {
    const aActive = activeUserIds.has(a?.id);
    const bActive = activeUserIds.has(b?.id);

    if (aActive !== bActive) return aActive ? -1 : 1;
    if (!aActive && !bActive) return 0;

    const aActiveStudent = activeByUserId.get(a?.id);
    const bActiveStudent = activeByUserId.get(b?.id);

    const aTime = new Date(aActiveStudent?.updated_at || aActiveStudent?.created_at || 0).getTime();
    const bTime = new Date(bActiveStudent?.updated_at || bActiveStudent?.created_at || 0).getTime();
    return bTime - aTime;
  });

  const content = (
    <div style={{ minWidth: 240 }}>
      {sortedClassStudents.length === 0 ? (
        <Typography.Text type="secondary">No active students</Typography.Text>
      ) : (
        <div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 120px",
              alignItems: "center",
              gap: 12,
              padding: "6px 0 8px 0",
              borderBottom: "1px solid #e5e7eb",
              marginBottom: 6,
            }}
          >
            <Typography.Text strong>Student Name</Typography.Text>
            <Typography.Text strong style={{ textAlign: "left" }}>
              Status
            </Typography.Text>
          </div>
          {sortedClassStudents.map((student) => {
            const isActive = activeUserIds.has(student?.id);
            const dotColor = isActive ? "#22c55e" : "#cbd5e1";
            const statusText = isActive ? "Active" : "Inactive";

            const studentActiveData = activeByUserId.get(student?.id);
            const displayName =
              studentActiveData?.user_name || formatStudentName(student);

            return (
              <div
                key={student?.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 120px",
                  alignItems: "center",
                  gap: 12,
                  padding: "8px 0",
                }}
              >
                <Typography.Text>{displayName}</Typography.Text>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      backgroundColor: dotColor,
                      display: "inline-block",
                      boxShadow: isActive
                        ? "0 0 0 2px rgba(34, 197, 94, 0.15)"
                        : "none",
                    }}
                  />
                  <Typography.Text type={isActive ? undefined : "secondary"}>
                    {statusText}
                  </Typography.Text>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
      <b>{activeStudents.length}</b>
      <span>joined students</span>
      <Popover
        content={content}
        trigger="click"
        placement="bottomRight"
        styles={{ body: { height: 338, overflow: "auto" } }}
      >
        <InfoCircleOutlined style={{ cursor: "pointer", color: "#1677ff", fontSize: 18 }} />
      </Popover>
    </div>
  );
};

export default JoinedStudents;